<template>
    <div class="container" id="slot-machine">
        <div class="page-content mt-10">
            <div class="row">
                <div class="col-12">
                    <h1 class="page-heading text-dark">
                        {{ $t('slot.title') }}
                    </h1>
                </div>

                <div class="col-12 position-relative mt-2">
                    <canvas
                        ref="linesCanvas"
                        :width="canvasWidth"
                        :height="canvasHeight"
                        class="lines-overlay"
                    ></canvas>

                    <div class="col-12">
                        <!-- Slot Machine -->
                        <div class="slot-machine">
                            <div class="slots">
                                <div
                                    v-for="(symbol, index) in slots"
                                    :key="index"
                                    class="slot"
                                    :class="{ 'highlight': highlightedPositions.includes(index), 'win-highlight': winningPositions.includes(index), 'wiggle': enableSound }"
                                    :style="isSlotSpinning(index) ? 'background-color: black' : ''"
                                >
                                    <img :src="getSlotSymbol(symbol, index)" alt="" style="height: 2em"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-12 row px-0">
                    <div class="col-4 text-end pt-2">
                        <a href="javascript:" class="w-100" v-on:click="adjustBet(false)">
                            <i class="fa fa-minus-circle" style="font-size: 3em; color: red"></i>
                        </a>
                    </div>
                    <div class="col-4 text-center"><h2>{{ bet }}</h2><small class="muted">{{ $t('slot.bet') }}</small></div>
                    <div class="col-4 text-left pt-2">
                        <a href="javascript:" class="w-100" v-on:click="adjustBet(true)">
                            <i class="fa fa-plus-circle" style="font-size: 3em; color: lightgreen"></i>
                        </a>
                    </div>
                </div>

                <div class="col-12 mt-3">
                    <button class="btn btn-warning w-100" style="height: 5em;" @click="spin" v-if="!isSpinning">{{ $t('slot.spin') }}</button>
                    <button class="btn btn-warning w-100" style="height: 5em;" v-else disabled>{{ $t('slot.spinning') }}</button><br/>
                </div>

                <div class="col-4 mt-1">
                    <div class="form-check form-switch mt-2 mr-2">
                        <input class="form-check-input" type="checkbox" id="fastSpin" v-model="fastSpin">
                        <label class="form-check-label" for="fastSpin"> {{ $t('slot.fastSpin') }}</label>
                    </div>
                </div>

                <div class="col-4 mt-1">
                    <div class="form-check form-switch mt-2 mr-2">
                        <input class="form-check-input" type="checkbox" id="enableSound" v-model="enableSound">
                        <label class="form-check-label" for="enableSound"> {{ $t('slot.sound') }}</label>
                    </div>
                </div>

                <div class="col-4 mt-1">
                    <div v-if="lastWin > 0" class="p-2">
                        <h6>{{ $t('slot.lastWin') }}</h6>
                        {{ lastWin }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "SlotMachine",
    data() {
        return {
            colors: [
                "#FF5733", "#33FF57", "#3357FF", "#FF33A8",
                "#A833FF", "#57FFA8", "#33D4FF", "#FF5733",
                "#C70039", "#900C3F", "#581845", "#FF5733",
                "#C70039", "#900C3F", "#581845", "#1ABC9C",
                "#2ECC71", "#3498DB", "#9B59B6", "#34495E",
                "#16A085", "#27AE60", "#2980B9", "#8E44AD",
                "#2C3E50", "#F39C12"
            ],
            SYMBOLS: [],
            WILD_SYMBOL: '133_dipocaco', // ditto as Wildcard
            slots: Array(15).fill('133_dipocaco'),
            payLines: [],
            win: 0,
            bet: 10,
            highlightedPositions: [],
            winningPositions: [],
            winningLines: [],
            resultMessage: "",
            canvasWidth: 300, // Adjust to match slot-machine width
            canvasHeight: 150, // Adjust to match slot-machine height
            rows: [
                [0, 5, 10],
                [1, 6, 11],
                [2, 7, 12],
                [3, 8, 13],
                [4, 9, 14],
            ],
            stoppedLines: false,
            fastSpin: false,
            enableSound: false,
            lastWin: 0,
            multipliers: {},
            musicPlayer: null,
            maxBet: 100000,
        };
    },
    mounted() {
        this.$store.commit('updateUserData');
        this.adjustCanvasSize();
        window.addEventListener('resize', this.adjustCanvasSize);

        document.querySelector('#slot-machine').addEventListener('blur', () => {
            this.enableSound = false;
            this.musicPlayer = null;
        });
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.adjustCanvasSize);
    },
    destroy() {
        this.enableSound = false;
    },
    computed: {
        rowSpinInterval() {
            return this.fastSpin ? 100 : 900;
        },
        isSpinning() {
            return this.stoppedLines !== false;
        }
    },
    watch: {
        enableSound(state, preState) {
            if (state === true) {
                this.initializeMusicPlayer();
            }

            if (state === false && preState === true) {
                this.musicPlayer.pause();
                this.musicPlayer = null;
            }
        },
    },
    methods: {
        spin() {
            let spinSound = new Audio('/assets/dipocaco/sounds/music/hgss_voltorb_flip_start.mp3');
            spinSound.volume = 0.25;

            spinSound.addEventListener('play', () => {
                this.musicPlayer.pause();
            });

            spinSound.addEventListener('ended', () => {
                this.musicPlayer.play();
            });

            let winSound = new Audio('/assets/dipocaco/sounds/music/hgss_obtained_item.mp3');
            winSound.volume = 0.25;

            winSound.addEventListener('play', () => {
                this.musicPlayer.pause();
            });

            winSound.addEventListener('ended', () => {
                this.musicPlayer.play();
            });

            if (this.bet > this.$store.state.user.credit) {
                iziToast.warning({title: this.$i18n.t('slot.noCredit')});
                return;
            }
            this.$store.commit('updateUserCredits');

            this.clearHighlights();
            this.clearCanvas();
            this.winningLines = [];
            this.winningPositions = [];

            axios.post('/api/casino/slot/play', {
                bet: this.bet
            }).then((response) => {
                this.multipliers = response.data.multipliers;
                this.payLines = response.data.payLines;
                this.SYMBOLS = response.data.symbols;
                this.slots = response.data.slots;
                this.stoppedLines = 0;

                setTimeout(() => {
                    this.stoppedLines = 1;
                }, this.rowSpinInterval * 1);

                setTimeout(() => {
                    this.stoppedLines = 2;
                    // if (this.enableSound) {
                    //     spinSound.play();
                    // }
                }, this.rowSpinInterval * 2);

                setTimeout(() => {
                    this.stoppedLines = 3;
                }, this.rowSpinInterval * 3);

                setTimeout(() => {
                    this.stoppedLines = 4;
                    this.$store.commit('updateUserCredits', true)
                }, this.rowSpinInterval * 4);

                setTimeout(() => {
                    this.stoppedLines = false;
                    this.winningLines = response.data.winningLines
                    this.winningPositions = response.data.winningPositions
                    this.lastWin = response.data.win;
                    this.drawWinningLines();

                    if (this.winningLines.length) {
                        iziToast.success({
                            title: this.$i18n.t('slot.noCredit', {win: response.data.win}),
                            position: 'topCenter',
                            timeout: 2500
                        });

                        if (this.enableSound) {
                            winSound.play();
                        }
                    } else {
                        // iziToast.info({title: "Better luck next time!", position: 'topCenter'});
                    }
                }, this.rowSpinInterval * 5);
            })
        },
        isSlotSpinning(index) {
            return this.isSpinning && this.stoppedLines < this.getRowByIndex(index);
        },
        getSlotSymbol(symbol, index) {
            if (this.isSlotSpinning(index)) {
                let rumblingAnimation = index >= 10 ? 3 : index >= 5 ? 2 : 1;
                return '/assets/dipocaco/slot_rumbling_'+rumblingAnimation+'.gif';
            }

            return '/assets/dipocaco/avatars/' + symbol + '.png';
        },
        adjustBet(up = true) {
            let amount = 10;

            if (this.bet >= 10000) {
                amount = up ? 10000 : this.bet === 10000 ? -1000 : -10000;
            } else if (this.bet >= 1000) {
                amount = up ? 1000 : this.bet === 1000 ? -100 : -1000;
            } else if (this.bet >= 100) {
                amount = up ? 100 : this.bet === 100 ? -10 : -100;
            } else {
                amount = up ? 10 : -10;
            }

            this.bet += amount;

            if (this.bet > this.maxBet) {
                this.bet = this.maxBet;
                iziToast.info({title: this.$i18n.t('slot.maxBet')})
            }

            if (this.bet < 10) {
                this.bet = 10;
                iziToast.info({title: this.$i18n.t('slot.minBet')})
            }
        },
        getRowByIndex(index) {
            let rowIndex = 0;

            this.rows.forEach((row, i) => {
                if (row.includes(index)) {
                    rowIndex = i;
                }
            });

            return rowIndex+1;
        },
        adjustCanvasSize() {
            const container = this.$el.querySelector('.slots');
            const canvas = this.$refs.linesCanvas;
            canvas.width = container.offsetWidth;
            canvas.height = container.offsetHeight;
        },
        clearCanvas() {
            const canvas = this.$refs.linesCanvas;
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        },
        drawWinningLines() {
            this.adjustCanvasSize(); // Aktualisiere Canvas-Größe vor dem Zeichnen
            const canvas = this.$refs.linesCanvas;
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            this.winningLines.forEach((lineObj) => {
                const line = this.payLines[lineObj.index]; // Hole das Linienarray basierend auf dem Index
                if (!line) return; // Sicherheit gegen undefinierte Linien

                ctx.beginPath();
                const startPos = this.getSlotPosition(line[0]); // Startpunkt der Linie
                ctx.moveTo(startPos.x, startPos.y);

                line.forEach((pos) => {
                    const slotPos = this.getSlotPosition(pos); // Slot-Position berechnen
                    ctx.lineTo(slotPos.x, slotPos.y);
                });

                ctx.strokeStyle = this.colors[Math.floor(Math.random() * this.colors.length)];
                ctx.lineWidth = 4;
                ctx.stroke();
            });
        },
        highlightPayline(index) {
            this.highlightedPositions = this.payLines[index];
        },
        clearHighlights() {
            this.highlightedPositions = [];
        },
        getSlotPosition(index) {
            const slotElements = document.querySelectorAll('.slot'); // Slots aus dem DOM
            const slot = slotElements[index].getBoundingClientRect(); // Slot-Größe & Position
            const container = this.$refs.linesCanvas.getBoundingClientRect(); // Canvas-Größe & Position

            return {
                x: slot.left - container.left + slot.width / 2,
                y: slot.top - container.top + slot.height / 2,
            }
        },
        initializeMusicPlayer() {
            this.musicPlayer = new Audio('/assets/dipocaco/sounds/music/hgss_goldenrod_game_corner.mp3');
            this.musicPlayer.volume = 0.2;
            this.musicPlayer.loop = true;
            this.musicPlayer.play();
        }
    }
};
</script>

<style scoped lang="scss">
.page-heading {
    background-image: url("/assets/dipocaco/pocas/casino-poca.jpg");
    background-position: center 30%;
}

.slot-machine {
    position: relative;
}

.slots {
    text-align: center;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
    margin-bottom: 20px;
}

.slot {
    font-size: 2rem;
    background: #e3e3e3;
    padding-top: 1.5em;
    padding-bottom: 1.5em;
    border-radius: 5px;
    text-align: center;
    max-width: 20vw;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    flex-wrap: nowrap;
    transition: background-color 0.3s ease;
}

.highlight {
    background-color: #90ee90;
}

.win-highlight {
    background-color: #ffd700;
}

.account {
    font-size: 1.2rem;
    margin-bottom: 10px;
}

.info, .result {
    margin-top: 15px;
    font-size: 1rem;
    color: #333;
}

.lines-overlay {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    pointer-events: none;
}

@keyframes wiggle {
    0% { transform: rotate(0deg); }
    25% { transform: rotate(-5deg); }
    50% { transform: rotate(5deg); }
    75% { transform: rotate(-5deg); }
    100% { transform: rotate(0deg); }
}

.wiggle img {
    //display: inline-block; /* Ensures transform works properly */
    animation: wiggle 0.5s infinite ease-in-out;
}

</style>
